import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion, fetchConfig } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const sourceUrl = productCard?.productPageUrl || hotspotProduct?.getHotspotProductLink()?.href || this.dataset.productUrl;
    if (!sourceUrl) return '';

    // Quick View always starts from the product's canonical state. A card can
    // retain a shopper's last shade in `?variant=`, but carrying that state
    // into the modal makes the checked swatch and its visible media race.
    const url = new URL(sourceUrl, window.location.origin);
    url.searchParams.delete('variant');
    url.searchParams.delete('option_values');
    return url.toString();
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    document.dispatchEvent(new CustomEvent('indri:card-options-close'));

    if (
      this.dataset.upsellSelectionSource === 'true' &&
      event.target instanceof HTMLButtonElement &&
      event.target.getAttribute('aria-pressed') === 'true'
    ) {
      document.dispatchEvent(
        new CustomEvent('indri:complementary-toggle-request', {
          detail: {
            ownerId: this.dataset.upsellOwnerId,
            productId: this.dataset.productId,
          },
        })
      );
      return;
    }

    // Quick View deliberately does not inherit the card's pending swatch
    // request. It opens from the canonical first-swatch state immediately.
    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog) {
      dialogComponent.setUpsellSelectionContext(
        this.dataset.upsellSelectionSource === 'true'
          ? {
              ownerId: this.dataset.upsellOwnerId || '',
              productId: this.dataset.productId || '',
              productTitle: this.dataset.productTitle || '',
              selectionLabel: this.dataset.selectionLabel || Theme.translations.add,
              selectionError: this.dataset.selectionError || '',
            }
          : null
      );
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Shopify's native accelerated checkout component intentionally creates a
    // checkout for the selected item only. Quick View must instead use the
    // shopper's complete cart, so replace that component before its contents
    // are moved into the live dialog. The wrapper and its `ref` stay intact,
    // allowing product-form.js to continue toggling it for unavailable variants.
    this.#replaceQuickViewCheckoutButtons(productGrid);

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#selectDefaultQuickViewSwatch(modalContent);
  }

  /** @param {Element} productGrid */
  #replaceQuickViewCheckoutButtons(productGrid) {
    const buttonLabel = document.documentElement.lang.startsWith('lt') ? 'Pirkti dabar' : 'Buy it now';

    productGrid.querySelectorAll('.accelerated-checkout-block').forEach((checkoutBlock) => {
      if (!(checkoutBlock instanceof HTMLElement)) return;

      const nativeButton = checkoutBlock.querySelector('shopify-accelerated-checkout, .shopify-payment-button');
      if (!nativeButton) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button quick-view-buy-now-button';
      button.dataset.quickViewBuyNow = 'true';
      button.textContent = buttonLabel;

      checkoutBlock.replaceChildren(button);
    });
  }

  /**
   * Makes the first selectable swatch the Quick View default and always
   * resolves its Shopify media. Some product payloads render their first
   * picker value before the matching featured-media response, so merely
   * checking the radio would leave a stale image visible.
   * @param {Element} modalContent - The modal content element
   */
  #selectDefaultQuickViewSwatch(modalContent) {
    const picker = modalContent.querySelector('variant-picker');
    if (!picker) return;

    const firstSwatch = this.#getFirstAvailableSwatch(picker);
    if (!(firstSwatch instanceof HTMLInputElement)) return;

    // Call the picker directly rather than relying on a synthetic radio click:
    // morph can insert this content before its delegated change listener has
    // reconnected. This guarantees one authoritative Section Rendering request
    // for the default swatch and its featured media.
    if (picker instanceof VariantPicker) {
      picker.variantChanged({ target: firstSwatch });
      return;
    }

    firstSwatch.checked = true;
    firstSwatch.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Finds the first valid shade option without mistaking a measurement (for
   * example 15 ml) for a swatch. Shopify marks impossible option combinations
   * with data-option-available="false" instead of disabling their radio.
   * @param {VariantPicker} picker
   * @returns {HTMLInputElement | null}
   */
  #getFirstAvailableSwatch(picker) {
    const swatchFieldset = Array.from(picker.querySelectorAll('fieldset')).find((fieldset) =>
      fieldset.querySelector('.variant-option__button-label--has-swatch input[type="radio"]')
    );
    const scope = swatchFieldset || picker;

    return /** @type {HTMLInputElement | null} */ (
      scope.querySelector(
        'input[type="radio"][data-option-value-id]:not([data-option-available="false"]):not([aria-disabled="true"]):not(:disabled)'
      )
    );
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {{ownerId: string, productId: string, productTitle: string, selectionLabel: string, selectionError: string} | null} */
  #upsellSelectionContext = null;
  /** @type {Promise<any> | null} */
  #upsellSelectionPromise = null;
  /** @type {any | null} */
  #upsellSelectionResource = null;
  #quickViewCheckoutInFlight = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener('submit', this.#handleUpsellSelectionSubmit, { capture: true });
    this.addEventListener('click', this.#handleQuickViewBuyNow, { capture: true });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener('submit', this.#handleUpsellSelectionSubmit, { capture: true });
    this.removeEventListener('click', this.#handleQuickViewBuyNow, { capture: true });
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Turns the existing Quick View into a variant-selection surface for one complementary item.
   * A null context restores normal add-to-cart behavior.
   * @param {{ownerId: string, productId: string, productTitle: string, selectionLabel: string, selectionError: string} | null} context
   */
  setUpsellSelectionContext(context) {
    this.#upsellSelectionContext = context;
    this.#upsellSelectionPromise = null;
    // Quick View renders Shopify's first available variant by default. In selection mode that is
    // presentation only: require a real ProductSelectEvent before accepting a multi-variant item.
    this.#upsellSelectionResource = null;
    this.toggleAttribute('data-upsell-selection-mode', Boolean(context));
    this.#updateUpsellSelectionCta();
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    if (this.#upsellSelectionContext) this.#upsellSelectionPromise = event.promise;

    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        // Changing a measurement can make the currently selected shade
        // impossible. Keep the shade when it exists in the new size; otherwise
        // resolve to that size's first available swatch and let its normal
        // ProductSelectEvent update media, price and the extra gallery.
        this.#resolveUnavailableQuickViewSwatch();

        if (this.#upsellSelectionContext) {
          this.#upsellSelectionResource = detail.resource || null;
          requestAnimationFrame(() => this.#updateUpsellSelectionCta());
        }

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #resolveUnavailableQuickViewSwatch() {
    const picker = this.querySelector('variant-picker');
    if (!(picker instanceof VariantPicker)) return;

    const swatchFieldset = Array.from(picker.querySelectorAll('fieldset')).find((fieldset) =>
      fieldset.querySelector('.variant-option__button-label--has-swatch input[type="radio"]')
    );
    if (!swatchFieldset) return;

    const selectedSwatch = swatchFieldset.querySelector('input[type="radio"]:checked');
    if (!(selectedSwatch instanceof HTMLInputElement) || selectedSwatch.dataset.optionAvailable !== 'false') return;

    const firstAvailableSwatch = swatchFieldset.querySelector(
      'input[type="radio"]:not([data-option-available="false"]):not([aria-disabled="true"]):not(:disabled)'
    );
    if (firstAvailableSwatch instanceof HTMLInputElement) {
      picker.variantChanged({ target: firstAvailableSwatch });
    }
  }

  /**
   * Quick View's custom "Buy it now" appends the selected variant to the
   * shopper's existing cart and then opens that complete cart at checkout.
   * @param {MouseEvent} event
   */
  #handleQuickViewBuyNow = (event) => {
    const checkoutButton = event.target instanceof Element ? event.target.closest('[data-quick-view-buy-now]') : null;
    if (!(checkoutButton instanceof HTMLButtonElement) || !this.contains(checkoutButton)) return;

    const form = this.querySelector('product-form-component form');
    if (!(form instanceof HTMLFormElement) || !form.checkValidity()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.#quickViewCheckoutInFlight) return;
    this.#quickViewCheckoutInFlight = true;

    checkoutButton.disabled = true;

    const formData = new FormData(form);
    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message || 'Add to cart failed');
        window.location.assign(`${Theme.routes.cart_url}/checkout`);
      })
      .catch((error) => {
        this.#quickViewCheckoutInFlight = false;
        checkoutButton.disabled = false;
        const liveRegion = this.querySelector('product-form-component [ref="liveRegion"]');
        if (liveRegion) liveRegion.textContent = error?.message || ({ lv: 'Neizdevās pievienot preci grozam.', et: 'Toodet ei õnnestunud ostukorvi lisada.', en: 'Could not add the item to your cart.' }[document.documentElement.lang] || 'Nepavyko pridėti prekės į krepšelį.');
      });
  };

  /** @param {SubmitEvent} event */
  #handleUpsellSelectionSubmit = async (event) => {
    const context = this.#upsellSelectionContext;
    if (!context) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    try {
      if (this.#upsellSelectionPromise) {
        const result = await this.#upsellSelectionPromise;
        this.#upsellSelectionResource = result?.detail?.resource || null;
      }
    } catch {
      this.#announceUpsellSelectionError();
      return;
    }

    const resource = this.#upsellSelectionResource;
    const variantId = resource?.id != null ? String(resource.id) : '';
    if (!variantId || resource?.available === false) {
      this.#announceUpsellSelectionError();
      return;
    }

    document.dispatchEvent(
      new CustomEvent('indri:complementary-variant-selected', {
        detail: {
          ownerId: context.ownerId,
          productId: context.productId,
          productTitle: context.productTitle,
          variantId,
          variantTitle: resource?.public_title || resource?.title || '',
          price: Number(resource?.price) || 0,
          compareAtPrice: Number(resource?.compare_at_price) || 0,
          available: resource?.available !== false,
        },
      })
    );

    this.closeDialog();
  };

  #updateUpsellSelectionCta() {
    if (!this.#upsellSelectionContext) return;
    const label = this.querySelector('.add-to-cart-text');
    if (label) label.textContent = this.#upsellSelectionContext.selectionLabel;
    const button = this.querySelector('product-form-component [ref="addToCartButton"]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = !this.#upsellSelectionResource || this.#upsellSelectionResource.available === false;
    }
  }

  #announceUpsellSelectionError() {
    const liveRegion = this.querySelector('product-form-component [ref="liveRegion"]');
    if (liveRegion) liveRegion.textContent = this.#upsellSelectionContext?.selectionError || '';
  }

  #handleDialogClose = () => {
    this.setUpsellSelectionContext(null);

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
